/**
 * Framework-agnostic theme module.
 *
 * Provides:
 *  - the resolved {@link lightTheme} / {@link darkTheme} objects and a
 *    {@link getTheme} resolver;
 *  - {@link themeToCssVars} / {@link cssVarsString} to emit CSS custom
 *    properties for the web (consumed under a `data-theme` attribute);
 *  - a renderer-agnostic React {@link ThemeProvider} + {@link useTheme} hook
 *    that simply carries the resolved {@link Theme} object via context.
 *
 * This module imports `react` (an optional peer) but never imports
 * `react-native` nor `react-dom`: RN apps reuse the very same theme object
 * through the same provider/hook.
 */

import React from 'react';
import {
  backofficeColors,
  colors,
  fontFamily,
  tokens,
  type ColorTokens,
  type Theme,
  type ThemeName,
} from '../tokens/index.js';

// ---------------------------------------------------------------------------
// Resolved themes
// ---------------------------------------------------------------------------

function buildTheme(name: ThemeName, color: ColorTokens): Theme {
  return {
    name,
    color,
    typography: tokens.typography,
    spacing: tokens.spacing,
    radii: tokens.radii,
  };
}

/** Fully resolved light theme. */
export const lightTheme: Theme = buildTheme('light', colors.light);

/** Fully resolved dark theme. */
export const darkTheme: Theme = buildTheme('dark', colors.dark);

/** All resolved themes keyed by scheme. */
export const themes: Readonly<Record<ThemeName, Theme>> = {
  light: lightTheme,
  dark: darkTheme,
};

/** Resolve a {@link Theme} by name. Defaults to light. */
export function getTheme(name: ThemeName = 'light'): Theme {
  return themes[name];
}

/**
 * The Backoffice's themes (OBR-132): the same light theme and the Backoffice's own dark. Only the
 * Backoffice reads these; every other app keeps {@link themes}.
 */
export const backofficeThemes: Readonly<Record<ThemeName, Theme>> = {
  light: buildTheme('light', backofficeColors.light),
  dark: buildTheme('dark', backofficeColors.dark),
};

/** Resolve a Backoffice {@link Theme} by name. Defaults to light. */
export function getBackofficeTheme(name: ThemeName = 'light'): Theme {
  return backofficeThemes[name];
}

// ---------------------------------------------------------------------------
// CSS custom properties (web)
// ---------------------------------------------------------------------------

/**
 * Prefix for every emitted CSS custom property. A color token `textPrimary`
 * becomes `--color-textPrimary`; components read it via
 * `var(--color-textPrimary)`.
 */
export const CSS_VAR_PREFIX = '--color-';

/**
 * Map a resolved theme's color tokens to a flat record of CSS custom
 * properties, e.g. `{ '--color-textPrimary': '#111417', … }`.
 *
 * Only colors are emitted as custom properties: spacing/radii/typography are
 * scheme-independent and best applied directly from the token modules, but a
 * theme switch only needs to swap the color layer.
 */
export function themeToCssVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme.color)) {
    vars[`${CSS_VAR_PREFIX}${key}`] = value;
  }
  return vars;
}

/**
 * Serialize a theme's color tokens to a CSS declaration block body, ready to
 * drop inside a selector. Example output (newline-separated):
 *   `--color-surface: #FFFFFF; --color-textPrimary: #111417; …`
 */
export function cssVarsString(theme: Theme): string {
  return Object.entries(themeToCssVars(theme))
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n');
}

/**
 * Build a full stylesheet string scoping each theme under a
 * `[data-theme="…"]` attribute selector, plus a `:root` default (light).
 * Inject this once (e.g. in a `<style>` tag) and toggle the active theme by
 * setting `document.documentElement.dataset.theme = 'dark'`.
 */
export function themeStylesheet(
  source: Readonly<Record<ThemeName, Theme>> = themes,
): string {
  const blocks: string[] = [`:root {\n${cssVarsString(source.light)}\n}`];
  for (const name of Object.keys(source) as ThemeName[]) {
    blocks.push(`[data-theme="${name}"] {\n${cssVarsString(source[name])}\n}`);
  }
  // Base type: Space Grotesk everywhere, including native form controls that
  // would otherwise fall back to the browser's system font.
  blocks.push(
    `body { font-family: ${fontFamily.sans}; }\nbutton, input, select, textarea { font-family: inherit; }`,
  );
  return blocks.join('\n\n');
}

// ---------------------------------------------------------------------------
// React context (renderer-agnostic)
// ---------------------------------------------------------------------------

const ThemeContext = React.createContext<Theme>(lightTheme);
ThemeContext.displayName = 'ObravaThemeContext';

/** Props for {@link ThemeProvider}. */
export interface ThemeProviderProps {
  /** Active color scheme. Ignored when an explicit `theme` is passed. */
  readonly name?: ThemeName;
  /** Pre-resolved theme object. Takes precedence over `name`. */
  readonly theme?: Theme;
  readonly children?: React.ReactNode;
}

/**
 * Provides the resolved {@link Theme} object to descendants via context.
 * Renderer-agnostic: works identically under react-dom and react-native.
 *
 * Web apps may additionally apply {@link themeStylesheet} and toggle
 * `data-theme`; this provider only carries the JS object that components read
 * through {@link useTheme}.
 */
export function ThemeProvider(props: ThemeProviderProps): React.ReactElement {
  const { name = 'light', theme, children } = props;
  const value = React.useMemo<Theme>(
    () => theme ?? getTheme(name),
    [theme, name],
  );
  return React.createElement(ThemeContext.Provider, { value }, children);
}

/**
 * Read the active {@link Theme} from context. Falls back to the light theme
 * when no provider is mounted, so isolated components never crash.
 */
export function useTheme(): Theme {
  return React.useContext(ThemeContext);
}

export type { Theme, ThemeName, ColorTokens };
